import Link from 'next/link';

import { Box, Text } from '@mantine/core';

import { PermissionProject, ProjectTeaser } from 'models';
import { TeamHelpers } from 'helpers/TeamHelpers';

import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { SettingsDropdown } from 'components/dropdown/SettingsDropdown';
import { useProjectMeta } from 'components/project-overview/hooks';
import { DeleteButton, DuplicateButton, MoveToTeamButton } from 'components/project-teaser/ProjectTeaser';
import { ProjectTeaserUpdateDot } from 'components/project-teaser/ProjectTeaserUpdateDot';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { useCurrentUser } from 'hooks/use-current-user';
import { useTeam } from 'hooks/use-team';

import type { RecentProject } from '@/collections/projects/endpoints/recent';

import { DashboardSkeletonBlock } from './DashboardSkeleton';

import cx from './Dashboard.module.scss';

const DashboardRecentDesignsTable = ({ projects }: { projects: RecentProject[] }) => (
    <Box className={`${cx.designTable} ${cxPanel.dividedList}`}>
        {projects.map((project) => (
            <RecentDesignRow key={project.id} project={project} />
        ))}
    </Box>
);

const RecentDesignRow = ({ project }: { project: RecentProject }) => {
    const { projectUrl, teamName, updatedWhen, isTeamLoading } = useProjectMeta(project);

    return (
        <Box component={Link} href={projectUrl} className={`${cxPanel.item} ${cx.designRow}`}>
            <Box miw={0}>
                <Text size="md" fw={600} truncate component="div">
                    {project.name} <ProjectTeaserUpdateDot project={project} lastView={project.lastView} />
                </Text>
                {isTeamLoading ? (
                    <DashboardSkeletonBlock height={10} width="50%" my={4} />
                ) : (
                    teamName && (
                        <Text size="xs" c="dimmed" truncate>
                            {teamName}
                        </Text>
                    )
                )}
            </Box>

            <Box miw={0} className={cx.designRowSecondary}>
                <Text size="sm" c="dimmed" truncate>
                    {updatedWhen}
                </Text>
            </Box>

            <DesignActions project={project} />
        </Box>
    );
};

const DesignActions = ({ project }: { project: ProjectTeaser }) => {
    const user = useCurrentUser();
    const teams = useCurrentTeams();
    const { team } = useTeam(project.team || undefined);

    const canMoveTeam = TeamHelpers.canMoveProject(team, user, teams);
    const canDelete = TeamHelpers.hasUserPermission(team, user, PermissionProject.DELETE);

    return (
        <Box onClick={(event) => event.preventDefault()}>
            <SettingsDropdown>
                <DuplicateButton project={project} />
                {canMoveTeam && <MoveToTeamButton project={project} />}
                {canDelete && <DeleteButton project={project} />}
            </SettingsDropdown>
        </Box>
    );
};

export { DashboardRecentDesignsTable };
